import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MenuButtonComponent } from '@sb/library/button';
import { SBDate } from '@sb/library/localization';
import { MbscDateType, MbscDatepickerCloseEvent } from '@mobiscroll/angular';
import { FormFieldDateBaseComponent } from '../form-field-date-base.component';

type Preset = 'nextmonth' | 'thismonth' | 'thisyear' | 'yeartodate' | 'today' | 'last7days' | 'last30days' | 'lastmonth' | 'lastyear';

@Component({
    selector: 'lib-input-date-range',
    templateUrl: './form-field-date-range.component.html',
    styleUrls: ['./form-field-date-range.component.scss']
})
export class FormFieldDateRangeComponent extends FormFieldDateBaseComponent<[SBDate, SBDate] | null> implements OnInit {
    @ViewChild(MenuButtonComponent) menuButton: MenuButtonComponent | undefined;

    @Input() showRangeLabels: boolean = true;
    @Input() showFutureDates: boolean = true;

    override readonly hasPrefixIconButton: boolean = true;

    ChangeEvent(change: any): void {
        if (change.value !== null && change.value[1] !== null) {
            const newStart = SBDate.FromJSDate(change.value[0]);
            const newEnd = SBDate.FromJSDate(change.value[1]);

            if (!this.formControl.value
                || newStart.totalMilliseconds !== this.formControl.value[0].totalMilliseconds
                || newEnd.totalMilliseconds !== this.formControl.value[1].totalMilliseconds) {

                this.SetFormControlValue([newStart, newEnd]);
                this.formControl.isDirty = true;
            }
        } else if (this.formControl.value != null) {
            this.SetFormControlValue(null);
            this.formControl.isDirty = true;
        }
    }

    CloseEvent(change: MbscDatepickerCloseEvent) {
        const value = change.value as MbscDateType[];
        if (!value?.[0] || !value?.[1]) {
            let startDate: SBDate;
            let endDate: SBDate;
            // if end date is not selected, default to 1 month after the start date.

            if (value[0] && !value[1]) {
                startDate = SBDate.FromJSDate(value[0] as Date);
                endDate = startDate.Clone().AddMonths(1);
            }
            this.SetFormControlValue((startDate! && endDate!) ? [startDate!, endDate!] : null);
            this.viewControl.setValue((startDate! && endDate!) ? [startDate!.ToISO(), endDate!.ToISO()] as any : null);
        }
        this.PopupClosed();
    }

    override SendBlur(): void {
        if (!this._popupOpen && !this._focused) {
            if (!this.formControl.value) {
                this.viewControl.setValue('');
            }

            super.SendBlur();
        }
    }

    override ObservePristine(): void {
        // This is done above in ChangeEvent instead
    }

    override writeValue(dateValue: [SBDate, SBDate] | null): void {
        if (dateValue) {
            this.viewControl.setValue([dateValue[0].ToISO(), dateValue[1].ToISO()] as any);
            setTimeout(() => this._lastInputValue = this.input!.nativeElement.value);
        } else {
            this.viewControl.setValue('');
        }

    }

    Preset(preset: Preset) {
        const today = SBDate.Today();
        let startDate: SBDate;
        let endDate: SBDate;

        switch (preset) {
            case 'nextmonth':
                startDate = SBDate.FromComponents(today.year, today.month, 1).AddMonths(1);
                endDate = SBDate.FromComponents(today.year, today.month, 1).AddMonths(2).AddDays(-1);
                break;
            case 'thismonth':
                startDate = SBDate.FromComponents(today.year, today.month, 1);
                endDate = SBDate.FromComponents(today.year, today.month, 1).AddMonths(1).AddDays(-1);
                break;
            case 'thisyear':
                startDate = SBDate.FromComponents(today.year, 1, 1);
                endDate = SBDate.FromComponents(today.year, 1, 1).AddYears(1).AddDays(-1);
                break;
            case 'yeartodate':
                startDate = SBDate.FromComponents(today.year, 1, 1);
                endDate = SBDate.Today();
                break;
            case 'last7days':
                startDate = SBDate.Today().AddDays(-7);
                endDate = SBDate.Today();
                break;
            case 'last30days':
                startDate = SBDate.Today().AddDays(-30);
                endDate = SBDate.Today();
                break;
            case 'lastmonth':
                startDate = SBDate.FromComponents(today.year, today.month, 1).AddMonths(-1);
                endDate = SBDate.FromComponents(today.year, today.month, 1).AddDays(-1);
                break;
            case 'lastyear':
                startDate = SBDate.FromComponents(today.year, 1, 1).AddYears(-1);
                endDate = SBDate.FromComponents(today.year, 1, 1).AddDays(-1);
                break;
            case 'today':
                startDate = SBDate.Today();
                endDate = SBDate.Today();
                break;
        }
        this.viewControl.setValue([startDate.ToISO(), endDate.ToISO()] as any);
        this.SetFormControlValue([startDate, endDate]);
        this.menuButton?.CloseMenu();
    }

    ClearSelection() {
        this.viewControl.setValue([null, null] as any);
        this.SetFormControlValue(null);
    }
}
